import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';
import type {
  GuideAccessDetails,
  GuideAccessInput,
  GuideAccessSaved,
  GuideBundle,
  GuideFaq,
  GuideFaqInput,
  GuideInput,
  GuideMedia,
  GuideMediaSlot,
  GuideTip,
  GuideTipInput,
  StaffGuide,
  StayGuideAccess,
} from '../interfaces/stays-guide';

/**
 * LOBBERO STAYS — the host's guest guide (plan S7.3 / S7.4 / S7.6;
 * lobbero_be/src/stays/routes/{guide,guide-media,guide-access}.js). The
 * DEFAULT axios instance like the other Stays clients (cookies, CSRF,
 * refresh). Every failure is a StaysApiError with the server's code and words
 * (a 409 STALE_GUIDE carries the current `version`, a 422 CODE_IN_TEXT the
 * `field`, both in `extra`).
 *
 * The host preview (GET …/guide/preview) is the preview tab's own call.
 *
 * NEVER LOG A RESPONSE OF getAccessDetails or getGuideAccess: they carry door
 * codes and the stay's PIN.
 */

const plainBody = (data: unknown): Record<string, unknown> | null =>
  data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)
    ? (data as Record<string, unknown>)
    : null;

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, plainBody(err.response.data), fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const enc = encodeURIComponent;
const listing = (id: string) => `${apiRoute}/stays/listings/${enc(id)}`;
const mediaBase = (listingId: string) => `${apiRoute}/stays/guide-media/${enc(listingId)}`;
const stay = (id: string) => `${apiRoute}/stays/reservations/${enc(id)}`;

// ── the guide ──────────────────────────────────────────────────────────────
export const getGuide = (listingId: string): Promise<GuideBundle> =>
  call(axios.get(`${listing(listingId)}/guide`), 'Could not load the guide.');

export const saveGuide = (listingId: string, input: GuideInput): Promise<{ guide: StaffGuide }> =>
  call(axios.put(`${listing(listingId)}/guide`, input), 'Could not save the guide.');

// ── the access details (values; stays:manage) ──────────────────────────────
export const getAccessDetails = (listingId: string): Promise<GuideAccessDetails> =>
  call(axios.get(`${listing(listingId)}/access-details`), 'Could not load the access details.');

export const saveAccessDetails = (listingId: string, input: GuideAccessInput): Promise<GuideAccessSaved> =>
  call(axios.put(`${listing(listingId)}/access-details`, input), 'Could not save the access details.');

// ── FAQs ───────────────────────────────────────────────────────────────────
export const createFaq = (listingId: string, input: GuideFaqInput): Promise<{ faq: GuideFaq }> =>
  call(axios.post(`${listing(listingId)}/faqs`, input), 'Could not add the question.');

export const updateFaq = (faqId: string, patch: GuideFaqInput): Promise<{ faq: GuideFaq }> =>
  call(axios.patch(`${apiRoute}/stays/faqs/${enc(faqId)}`, patch), 'Could not save the question.');

export const deleteFaq = (faqId: string): Promise<{ deleted: boolean }> =>
  call(axios.delete(`${apiRoute}/stays/faqs/${enc(faqId)}`), 'Could not delete the question.');

export const reorderFaqs = (listingId: string, ids: string[]): Promise<{ faqs: GuideFaq[] }> =>
  call(axios.patch(`${listing(listingId)}/faqs/order`, { ids }), 'Could not move the question.');

// ── local tips ─────────────────────────────────────────────────────────────
export const createTip = (listingId: string, input: GuideTipInput): Promise<{ tip: GuideTip }> =>
  call(axios.post(`${listing(listingId)}/tips`, input), 'Could not add the tip.');

export const updateTip = (tipId: string, patch: GuideTipInput): Promise<{ tip: GuideTip }> =>
  call(axios.patch(`${apiRoute}/stays/tips/${enc(tipId)}`, patch), 'Could not save the tip.');

export const deleteTip = (tipId: string): Promise<{ deleted: boolean }> =>
  call(axios.delete(`${apiRoute}/stays/tips/${enc(tipId)}`), 'Could not delete the tip.');

export const reorderTips = (listingId: string, ids: string[]): Promise<{ tips: GuideTip[] }> =>
  call(axios.patch(`${listing(listingId)}/tips/order`, { ids }), 'Could not move the tip.');

// ── photos ─────────────────────────────────────────────────────────────────
export interface GuideUploadOptions {
  slot: GuideMediaSlot;
  title?: string;
  category?: string;
  /** Gallery only: shown after check-in is complete. */
  sensitive?: boolean;
}

const extensionOf = (type: string): string =>
  type === 'image/jpeg' ? 'jpg' : type === 'image/png' ? 'png' : 'webp';

/**
 * One photo, multipart field `image` (the server's multer field). The text
 * fields go FIRST, so they are parsed before the file arrives. Content-Type is
 * NOT set: the browser adds the boundary itself (images-client.ts).
 */
export const uploadGuideMedia = (
  listingId: string,
  image: Blob,
  opts: GuideUploadOptions,
  onProgress?: (percent: number) => void
): Promise<{ media: GuideMedia }> => {
  const form = new FormData();
  form.append('slot', opts.slot);
  if (opts.title) form.append('title', opts.title);
  if (opts.category) form.append('category', opts.category);
  if (opts.sensitive !== undefined) form.append('sensitive', opts.sensitive ? 'true' : 'false');
  form.append('image', image, `photo.${extensionOf(image.type)}`);

  return call(
    axios.post(mediaBase(listingId), form, {
      onUploadProgress: event => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    }),
    'Could not upload the photo.'
  );
};

export const deleteGuideMedia = (listingId: string, mediaId: string): Promise<{ deleted: boolean }> =>
  call(axios.delete(`${mediaBase(listingId)}/${enc(mediaId)}`), 'Could not remove the photo.');

export const updateGuideMedia = (
  listingId: string,
  mediaId: string,
  patch: { title?: string | null; category?: string | null; sensitive?: boolean }
): Promise<{ media: GuideMedia }> =>
  call(axios.patch(`${listing(listingId)}/guide/media/${enc(mediaId)}`, patch), 'Could not save the photo.');

export const reorderGallery = (listingId: string, ids: string[]): Promise<{ media: GuideMedia[] }> =>
  call(axios.patch(`${listing(listingId)}/guide/media-order`, { slot: 'gallery', ids }), 'Could not move the photo.');

/** The photo's bytes from the host file route — what Share hands the device. */
export const fetchGuideMediaFile = (listingId: string, mediaId: string): Promise<Blob> =>
  call(axios.get(`${mediaBase(listingId)}/${enc(mediaId)}/file`, { responseType: 'blob' }), 'Could not get the photo.');

// ── the stay's guide PIN (S7.6) ──────────────────────────────────────────────
export const getGuideAccess = (reservationId: string): Promise<StayGuideAccess> =>
  call(axios.get(`${stay(reservationId)}/guide-access`), 'Could not load the guide PIN.');

export const setGuideAccess = (
  reservationId: string,
  input: { pin_required: boolean; regenerate?: boolean }
): Promise<StayGuideAccess> =>
  call(axios.put(`${stay(reservationId)}/guide-access`, input), 'Could not change the guide PIN.');

export const unlockGuideAccess = (reservationId: string): Promise<StayGuideAccess> =>
  call(axios.post(`${stay(reservationId)}/guide-access/unlock`), 'Could not unlock the guide.');
